'use client'

import { Swiper, SwiperSlide } from "swiper/react";
import { useEffect, useRef } from "react";
import type { Swiper as SwiperType } from "swiper/types";
import CompactButton from "@/components/ui/CompactButton/CompactButton";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Pagination, Autoplay, Mousewheel } from "swiper/modules";
import '@/components/ui/TestimonialCard/TestimonialCard.css';

import React from "react";
import Headline from "@/components/ui/Headline/Headline";
import { LanguageType, LanguageDirection, LPResultsSectionContent } from "@/lib/types";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import gsap from 'gsap';
import { useGSAP } from "@gsap/react";
import Subheading from "@/components/ui/Subheading/Subheading";
import Image from "next/image";

import { debug } from '@/lib/debug';
import { useIsLg, useIsLg2, useIsTb } from '@/contexts/BreakpointContext';
gsap.registerPlugin(ScrollTrigger);

interface LPResultsSectionProps {
  language?: LanguageType
  headlineText: string
  subheadingText: string
  onInitialPosition?: (position: number) => void
  lpResultsSectionPageData?: LPResultsSectionContent
  id?: string
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};

interface ImageCard {
  id: number;
  image1Url: string;
  image1AltText: string;
  image2Url: string;
  image2AltText: string;
  author: string;
  description: string;
  actionText: string;
  deviceImageUrl: string;
  deviceImageAltText: string;
}


const LPResultsSection: React.FC<LPResultsSectionProps> = ({
  language = "english",
  headlineText,
  subheadingText,
  onInitialPosition,
  lpResultsSectionPageData,
  id,
}) => {
  const isLg2 = useIsLg2();
  // const isLg = useIsLg2();
  const isTb = useIsTb();
  const isLg = useIsLg();
  const swiperRef = useRef<SwiperType>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.snapGrid = [...swiperRef.current.slidesGrid];
    }
    return () => {
      if (swiperRef.current) {
      }
    };
  }, []);

  const imageCards: ImageCard[] = lpResultsSectionPageData?.results.map((result: any, index: number) => ({
    id: index,
    image1Url: result.resultImage1Url,
    image1AltText: result.resultImage1AltText,
    image2Url: result.resultImage2Url,
    image2AltText: result.resultImage2AltText,
    author: result.resultAuthor,
    description: result.resultDescription,
    actionText: result.actionText,
    deviceImageUrl: result.deviceImageUrl,
    deviceImageAltText: result.deviceImageAltText
  })) ?? [];

  // debug.log('team section rendering');


  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };
  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.snapGrid = [...swiperRef.current.slidesGrid];

      // Listen for Swiper's init and update events
      // swiperRef.current.on('init', () => {
      //   ScrollTrigger.refresh();
      // });

      // swiperRef.current.on('resize', () => {
      //   ScrollTrigger.refresh();
      // });

      // swiperRef.current.on('update', () => {
      //   ScrollTrigger.refresh();
      // });

      // swiperRef.current.on('snapGridLengthChange', () => {
      //   ScrollTrigger.refresh();
      // });
    }

    return () => {
      if (swiperRef.current) {
        // Clean up event listeners
        // swiperRef.current.off('init');
        // swiperRef.current.off('snapGridLengthChange');
        // swiperRef.current.off('update');
        // ScrollTrigger.refresh();
      }
    };
  }, []);

  // Refresh when screen size changes affect card width
  // useEffect(() => {
  //   ScrollTrigger.refresh();
  // }, [isLg, isTb]);

  useEffect(() => {
    // ScrollTrigger.refresh();
    if (sectionRef.current && onInitialPosition) {
      const rect = sectionRef.current.getBoundingClientRect();
      const topY = window.scrollY + rect.top;
      if (!isLg2) {
        ScrollTrigger.refresh();
      }
      onInitialPosition(topY);
    }
  }, [isLg2, onInitialPosition]);

  useGSAP(() => {
    // Select all team cards
    const container = containerRef.current;
    if (container && isLg2) {

      const cards: HTMLElement[] = Array.from(document.querySelectorAll('.team-member-card'));

      gsap.fromTo(cards, {
        yPercent: -10
      }, {
        yPercent: 10, // Subtle upward movement
        ease: "none",
        force3D: true,
        scrollTrigger: {
          trigger: container,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
          invalidateOnRefresh: true,
          // markers: true,
          id: "team-section-scroll-trigger"
        },
        onEnter: () => {
          cards.forEach(card => {
            card.style.willChange = "transform";
          });
        },
        onLeave: () => {
          cards.forEach(card => {
            card.style.willChange = "auto";
          });
        },
        onEnterBack: () => {
          cards.forEach(card => {
            card.style.willChange = "transform";
          });
        },
        onLeaveBack: () => {
          cards.forEach(card => {
            card.style.willChange = "auto";
          });
        }
      });
    }
  }, { dependencies: [isLg2, isTb], revertOnUpdate: true }); // Scope the animations to the container

  return (
    // section has custom padding!
    <section
      ref={sectionRef}
      id={id}
      style={{ overscrollBehaviorX: 'none' }}
      className="
      w-full flex flex-col items-center justify-center
      gap-[1.5rem] lg2:gap-[2.5rem]
      overflow-hidden
      ">
      <div
        className="w-full flex justify-center
          padding-main-x-mobile
          padding-main-x-desktop
        ">
        <div
          className="w-full container-large"
        >

          <div
            style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
            className="
            w-full flex flex-col lg2:flex-row lg2:flex-wrap
            gap-y-[2rem] lg2:gap-y-[3.5rem] lg2:gap-x-[2rem]
            px-[0.5rem] lg2:px-0
        ">
            <Headline
              type="short"
              alignment={language === "english" ? 'left' : 'right'}
              heading={headlineText}
              subheading={subheadingText}
              language={language}
              shouldAnimate={false}
            />

            {isLg2 && (
              <div style={{ direction: "ltr" }} className="flex-grow min-w-[11.6875rem] w-[11.6875rem] max-w-full self-end">
                <div className={`flex flex-grow flex-row ${language === "english" ? 'justify-end' : 'justify-start'} gap-[0.75rem] lg2:gap-[1rem]`}>
                  <CompactButton
                    size={isLg2 ? 64 : 44}
                    radius={9999}
                    className="left-0 bottom-0"
                    onClick={language === "english" ? handlePrev : handleNext}
                  >
                    <CaretLeft />
                  </CompactButton>

                  <CompactButton
                    size={isLg2 ? 64 : 44}
                    radius={9999}
                    className="right-0 bottom-0"
                    onClick={language === "english" ? handleNext : handlePrev}
                  >
                    <CaretRight />
                  </CompactButton>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      <div className="w-full flex justify-center">
        <div
          ref={containerRef}
          style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
          className="w-full overflow-visible">
          <div className="w-full overflow-visible">
            <Swiper
              style={{
                width: '100%',
                overflow: 'visible'
              }}
              modules={[Pagination, Autoplay, Mousewheel]}
              pagination={{
                clickable: true,
                el: '.swiper-custom-pagination',
                bulletClass: 'swiper-pagination-bullet',
                bulletActiveClass: 'swiper-pagination-bullet-active',
              }}
              loop={imageCards.length > 1}
              spaceBetween={isTb ? 16 : 12}
              slidesPerView="auto"
              autoplay={{
                delay: 7000,
                disableOnInteraction: true,
              }}
              onBeforeInit={(swiper) => {
                swiperRef.current = swiper;
              }}
              centeredSlidesBounds={isTb ? false : true}
              centeredSlides={isTb ? false : true}
              observer={true}
              observeSlideChildren={true}
              resizeObserver={true}
              // mousewheel={{
              //   forceToAxis: true,
              //   sensitivity: 0.5,
              //   releaseOnEdges: true,
              // }}
              className="!overflow-visible testimonial-swiper pb-8 "
            >
              <div className="w-full overflow-hidden ">
                <div className="w-full">
                  <div className="flex w-full">
                    {imageCards.map((card) => (
                      <SwiperSlide
                        key={card.id}
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                        className="
                          cursor-grab
                          px-[0.5rem] tb:px-[3.5rem] lg2:px-[3.5rem]
                        "
                      >
                        <div className="w-full container-large
                        border-main border-stroke-soft-200 border-radius-main
                        p-[0.5rem] lg2:p-[1.5rem]
                        ">
                          <div className="w-full flex flex-col lg2:flex-row gap-[0rem] lg2:gap-[1rem]">
                            {/* Left side - Two images side by side */}
                            <div className="w-full lg2:w-2/3 flex flex-row gap-0 lg2:gap-[1rem]">
                              <div className={`
                                w-1/2 relative overflow-hidden aspect-[4/5]
                                ${language === "english" ? 'rounded-l-[24px] rounded-r-0 lg2:rounded-[32px]' : 'rounded-r-[24px] rounded-l-0 lg2:rounded-[32px]'}
                                border-main border-stroke-sub-300`}>
                                <Image
                                  src={card.image1Url}
                                  alt={card.image1AltText}
                                  fill
                                  sizes="(min-width: 1440px) 25vw, (min-width: 768px) 33vw, 40vw"
                                  loading="lazy"
                                  className="object-cover"
                                />
                                <div className={`
                                absolute
                                ${isLg2 ? (language === "english" ? 'top-[2rem] left-[2rem]' : 'top-[2rem] right-[2rem]') : (language === "arabic" ? 'bottom-[0.75rem] right-[0.75rem]' : 'bottom-[0.75rem] left-[0.75rem]')}
                                bg-primary-base border-main border-yellow-3
                                text-text-reversed px-[0.75rem] py-[0.375rem] lg2:py-[0.5rem]
                                rounded-[0.5rem] lg2:rounded-[0.75rem]
                                ${isLg2 ? 'text-20--alexandria' : 'text-14--alexandria'}
                                `}
                                >
                                  {language === 'arabic' ? 'قبل ريڤيڤ' : 'Before Rivive'}
                                </div>
                              </div>
                              <div className={`
                                w-1/2 relative overflow-hidden aspect-[4/5]
                                ${language === "english" ? 'rounded-r-[24px] rounded-l-0 lg2:rounded-[32px]' : 'rounded-l-[24px] rounded-r-0 lg2:rounded-[32px]'}
                                border-main border-stroke-sub-300`}>
                                <Image
                                  src={card.image2Url}
                                  alt={card.image2AltText}
                                  fill
                                  sizes="(min-width: 1440px) 25vw, (min-width: 768px) 33vw, 40vw"
                                  loading="lazy"
                                  className="object-cover"
                                />
                                <div className={`
                                absolute
                                ${isLg2 ? (language === "english" ? 'top-[2rem] left-[2rem]' : 'top-[2rem] right-[2rem]') : (language === "arabic" ? 'bottom-[0.75rem] right-[0.75rem]' : 'bottom-[0.75rem] left-[0.75rem]')}
                                bg-primary-base border-main border-yellow-3
                                text-text-reversed px-[0.75rem] py-[0.375rem] lg2:py-[0.5rem]
                                rounded-[0.5rem] lg2:rounded-[0.75rem]
                                ${isLg2 ? 'text-20--alexandria' : 'text-14--alexandria'}
                                `}>
                                  {language === 'arabic' ? 'بعد ريڤيڤ' : 'After Rivive'}
                                </div>
                              </div>
                            </div>

                            {/* Right side - Content column */}
                            <div className="w-full lg2:w-1/3 flex flex-col gap-[1.5rem] lg2:gap-[2rem] px-[0.5rem] pt-[1.5rem] pb-[1rem] lg2:p-[2rem] lg2:pb-[1.5rem] lg2:justify-between">
                              <div className="flex flex-col gap-[1.5rem] lg2:gap-[2rem] ">


                                <div
                                  // style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
                                  style={{ direction: "ltr" }}
                                  className={`
                                max-w-[44rem]
                                flex flex-col
                                gap-[1rem] lg2:gap-[1.5rem]
                                ${language === 'arabic' ? 'items-end text-right' : 'items-start text-left'}
                              `}>
                                  <p className="text-text-primary text-28-to-20--neue-june">
                                    {card.description}
                                  </p>

                                  <Subheading
                                    size={isLg ? 20 : 16}
                                    subheading={card.author}
                                    alignment={language === "english" ? "left" : "right"}
                                    textColor="text-text-quaternary"
                                  />
                                </div>

                                <div className="w-full h-[1.5px] bg-stroke-soft-200"></div>

                              </div>

                              <div className="w-full max-w-[44rem] flex flex-row gap-[1rem] items-center justify-start">
                                <div className="w-fit">
                                  <div className="
                                  h-[44px] w-[44px] lg2:h-[64px] lg2:w-[64px]
                                  rounded-[12px] outline-main outline-stroke-soft-200
                                  overflow-hidden
                                  ">
                                    <Image
                                    src={card.deviceImageUrl}
                                    alt={card.deviceImageAltText}
                                    width={64}
                                    height={64}
                                    loading="lazy"
                                    className="w-full h-full"
                                  />
                                  </div>

                                </div>
                                <p className="text-text-tertiary text-16-to-14--alexandria">
                                  {card.actionText}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </SwiperSlide>
                    ))}
                  </div>
                </div>
              </div>

              {imageCards.length > 1 && (
                <div
                  style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
                  className="
                mt-[1.5rem] lg2:mt-[2.5rem]
                mb-[0.25rem]
                w-full
                flex items-center justify-center gap-[1rem]
                px-[1.25rem] tb:px-[4.25rem] lg2:px-0">
                  {!isLg2 && (
                    <CompactButton
                      size={44}
                      radius={9999}
                      onClick={handlePrev}
                    >
                      {language === "english" ? <CaretLeft /> : <CaretRight />}
                    </CompactButton>
                  )}

                  <div className="swiper-custom-pagination flex justify-center gap-[0.625rem]" />

                  {!isLg2 && (
                    <CompactButton
                      size={44}
                      radius={9999}
                      onClick={handleNext}
                    >
                      {language === "english" ? <CaretRight /> : <CaretLeft />}
                    </CompactButton>
                  )}
                </div>
              )}
            </Swiper>
          </div>

        </div >
      </div >
    </section >
  );
}


export default LPResultsSection;
